import { useId, useState } from 'react';
import type { InputHTMLAttributes } from 'react';

interface AuthFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  /** Help text shown under the field while it has no error -- in the same slot, which an error then takes over. */
  hint?: string;
}

/**
 * Label + input + validation slot.
 *
 * No forwardRef: React 19 passes `ref` as a normal prop, so react-hook-form's
 * `{...register('email')}` spreads straight through.
 *
 * A `type="password"` field gets a show/hide control automatically. It is not
 * opt-in because there is no password field on these screens that should lack
 * one -- a user who cannot see what they typed retypes it, and on a phone
 * keyboard that is where most sign-in failures come from.
 */
export function AuthField({ label, error, hint, id, required, type, ...inputProps }: AuthFieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = `${fieldId}-error`;

  const isPassword = type === 'password';
  const [revealed, setRevealed] = useState(false);

  /*
    The toggle swaps the input's type, which is what actually unmasks the value;
    nothing is copied into component state, so the field stays uncontrolled and
    react-hook-form keeps owning it. Validation and submission are untouched --
    `type` is not part of what gets sent.
  */
  const inputType = isPassword && revealed ? 'text' : type;

  const input = (
    <input
      id={fieldId}
      className={`form-field__input${isPassword ? ' form-field__input--with-affix' : ''}`}
      type={inputType}
      required={required}
      aria-invalid={error ? true : undefined}
      aria-describedby={error || hint ? errorId : undefined}
      {...inputProps}
    />
  );

  return (
    <div className="form-field">
      <label className="form-field__label" htmlFor={fieldId}>
        {label}
        {required ? (
          <span className="form-field__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <div className="form-field__control">
        {/*
          The wrapper exists only for a password field, and only so the button
          has something to be positioned against. A field without one renders
          exactly as it did before.
        */}
        {isPassword ? (
          <div className="form-field__affix">
            {input}
            <button
              type="button"
              className="form-field__reveal"
              onClick={() => {
                setRevealed((shown) => !shown);
              }}
              /*
                Named for what the control does, not for what it shows: a screen
                reader announcing "eye icon" tells the user nothing. `aria-pressed`
                carries the current state so the label does not have to repeat it.
              */
              aria-label={revealed ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
              aria-pressed={revealed}
              aria-controls={fieldId}
              // Skipped in the tab order: the field, then the submit. Reachable by
              // click and by screen reader, but it does not sit between the two.
              tabIndex={-1}
            >
              <i className={`pi ${revealed ? 'pi-eye-slash' : 'pi-eye'}`} aria-hidden="true" />
            </button>
          </div>
        ) : (
          input
        )}
        {/*
          Always rendered so the reserved height keeps the submit button from
          jumping when a message appears. The comp allotted this row too --
          it just showed the red helper text unconditionally.
        */}
        <p
          className={`form-field__error${!error && hint ? ' form-field__hint' : ''}`}
          id={errorId}
          role={error ? 'alert' : undefined}
        >
          {error ?? hint ?? ''}
        </p>
      </div>
    </div>
  );
}
